import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFlags, useLabels, usePrefs, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { trackEvent } from 'ddc-track-event';
import { getFlagValue } from '../utilities/flags';
import { addImageParams } from '../utilities/images';
import { publishVehicleData } from '../utilities/vehicleTracking';

const VehicleImage = ({
	image,
	imageIndex,
	enableImage,
	onImageLoad,
	placeholderHeight,
	link,
	uuid
}) => {
	const { windowId, widgetName } = useRequestData();
	const {
		altAttributeBefore,
		altAttributeAfter,
		titleAttributeBefore,
		titleAttributeAfter
	} = usePrefs();
	const flags = useFlags();
	const labels = useLabels();
	const imageRef = useRef();
	const [isInitialLoad, setIsInitialLoad] = useState(false);
	const [placeholderStyles, setPlaceholderStyles] = useState(null);

	const { uri, alt, title } = image;

	const imageFetchPriority = getFlagValue(
		flags,
		'srp-image-fetch-priority',
		'_toggleSRPImageFetchPriority'
	);

	let imageLoadingPriority = getFlagValue(
		flags,
		'srp-image-loading-priority',
		'_toggleSRPImageLoadingPriority'
	);

	setNewRelicCustomAttribute('srpImageFetchPriority', imageFetchPriority);
	setNewRelicCustomAttribute('srpImageLoadingPriority', imageLoadingPriority); // We want to track the LD value, not the final value.

	// Existing behavior as a fallback in case something goes awry.
	if (imageLoadingPriority === 'original') {
		const canNativeLazyLoad = 'loading' in HTMLImageElement.prototype;
		const shouldNativeLazyLoad = canNativeLazyLoad && imageIndex === 0; // only need to lazy load the first image in the carousel
		imageLoadingPriority = shouldNativeLazyLoad ? 'lazy' : null;
	}

	const src = addImageParams(uri);

	useEffect(() => {
		if (isInitialLoad) {
			setPlaceholderStyles(null);
		} else if (placeholderHeight) {
			setPlaceholderStyles({
				height: placeholderHeight,
				overflow: 'hidden',
				padding: 0
			});
		}
	}, [isInitialLoad, placeholderHeight]);

	if (enableImage && image) {
		const onLoad = () => {
			if (imageRef.current.src) {
				setIsInitialLoad(true);
				onImageLoad(imageRef);
			}
		};

		return (
			<a
				href={link}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Vehicle photo',
						result: 'Navigated to VDP'
					});
					publishVehicleData(uuid);
				}}
			>
				<div
					className={setClassNames([
						'img-container',
						'img-placeholder',
						isInitialLoad ? 'img-loaded' : null
					])}
					style={placeholderStyles}
				>
					<img
						loading={imageLoadingPriority}
						// eslint-disable-next-line react/no-unknown-property
						fetchpriority={
							imageFetchPriority !== 'auto'
								? imageFetchPriority
								: null
						}
						ref={imageRef}
						alt={setClassNames([
							labels.get(altAttributeBefore),
							alt,
							labels.get(altAttributeAfter)
						])}
						title={setClassNames([
							labels.get(titleAttributeBefore),
							title,
							labels.get(titleAttributeAfter)
						])}
						onLoad={onLoad}
						onError={onLoad}
						className="w-100"
						src={src}
					/>
				</div>
			</a>
		);
	}

	return null;
};

VehicleImage.propTypes = {
	image: PropTypes.shape({
		uri: PropTypes.string,
		alt: PropTypes.string,
		title: PropTypes.string
	}).isRequired,
	imageIndex: PropTypes.number.isRequired,
	enableImage: PropTypes.bool,
	onImageLoad: PropTypes.func,
	placeholderHeight: PropTypes.number,
	link: PropTypes.string,
	uuid: PropTypes.string.isRequired
};

VehicleImage.defaultProps = {
	enableImage: false,
	onImageLoad: () => null,
	placeholderHeight: null,
	link: ''
};

export default VehicleImage;
